import { useTheme } from 'next-themes';
import { useSyncExternalStore, type ComponentProps, type ReactNode } from 'react';
import { Toaster as Sonner } from 'sonner';

import { TOAST_MAX_VISIBLE } from './constants/sonner-variants-constants';
import { TOAST_DURATION_MS, TOAST_PLACEMENT } from './types/sonner-variants-enums';

type ToasterProps = ComponentProps<typeof Sonner>;

const MOBILE_TOAST_QUERY = '(max-width: 767px)';

function subscribeMobileToastPlacement(onStoreChange: () => void): () => void {
  const mediaQuery = window.matchMedia(MOBILE_TOAST_QUERY);
  mediaQuery.addEventListener('change', onStoreChange);
  return () => mediaQuery.removeEventListener('change', onStoreChange);
}

function getMobileToastPlacementSnapshot(): boolean {
  return window.matchMedia(MOBILE_TOAST_QUERY).matches;
}

function getMobileToastPlacementServerSnapshot(): boolean {
  return false;
}

function useMobileToastPlacement(): boolean {
  return useSyncExternalStore(
    subscribeMobileToastPlacement,
    getMobileToastPlacementSnapshot,
    getMobileToastPlacementServerSnapshot
  );
}

const toastToneClassNames = {
  toast:
    'group toast group-[.toaster]:rounded-overlay group-[.toaster]:border-border-default group-[.toaster]:bg-surface-raised group-[.toaster]:text-content-primary group-[.toaster]:shadow-overlay',
  title:
    'group-[.toast]:type-body group-[.toast]:font-medium group-[.toast]:text-content-primary',
  description:
    'group-[.toast]:type-field-description group-[.toast]:font-normal group-[.toast]:text-content-secondary',
  icon: 'group-[.toast]:shrink-0',
  closeButton:
    'group-[.toast]:border-border-default group-[.toast]:bg-surface-base group-[.toast]:text-content-secondary group-[.toast]:shadow-surface group-[.toast]:transition-colors group-[.toast]:duration-standard group-[.toast]:focus-visible:ring-2 group-[.toast]:focus-visible:ring-focus-ring group-[.toast]:hover:bg-neutral-interaction-hover group-[.toast]:hover:text-content-primary [@media(pointer:coarse)]:size-11',
  actionButton:
    'group-[.toast]:rounded-control group-[.toast]:bg-action-primary group-[.toast]:type-field-label group-[.toast]:font-medium group-[.toast]:text-action-primary-foreground group-[.toast]:transition-colors group-[.toast]:duration-standard group-[.toast]:focus-visible:ring-2 group-[.toast]:focus-visible:ring-focus-ring group-[.toast]:hover:bg-action-primary-hover',
  cancelButton:
    'group-[.toast]:rounded-control group-[.toast]:border group-[.toast]:border-border-default group-[.toast]:bg-surface-base group-[.toast]:type-field-label group-[.toast]:font-medium group-[.toast]:text-content-primary group-[.toast]:shadow-surface group-[.toast]:transition-colors group-[.toast]:duration-standard group-[.toast]:focus-visible:ring-2 group-[.toast]:focus-visible:ring-focus-ring group-[.toast]:hover:bg-neutral-interaction-hover',
  default: '[&_[data-icon]]:text-content-secondary',
  success: '[&_[data-icon]]:text-status-success-foreground',
  info: '[&_[data-icon]]:text-status-info-foreground',
  warning: '[&_[data-icon]]:text-status-warning-foreground',
  error: '[&_[data-icon]]:text-status-danger-foreground',
  loading: '[&_[data-icon]]:text-content-secondary',
} as const;

function Toaster({ ...props }: ToasterProps): ReactNode {
  const { theme = 'system' } = useTheme();
  const isMobilePlacement = useMobileToastPlacement();

  const {
    position,
    visibleToasts,
    duration,
    mobileOffset,
    toastOptions,
    closeButton,
    ...rest
  } = props;

  return (
    <Sonner
      className="toaster group"
      theme={theme as ToasterProps['theme']}
      position={position ?? (isMobilePlacement ? TOAST_PLACEMENT.MOBILE : TOAST_PLACEMENT.DESKTOP)}
      visibleToasts={visibleToasts ?? TOAST_MAX_VISIBLE}
      duration={duration ?? TOAST_DURATION_MS.SUCCESS}
      closeButton={closeButton ?? true}
      mobileOffset={
        mobileOffset ?? {
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
        }
      }
      toastOptions={{
        ...toastOptions,
        classNames: {
          ...toastToneClassNames,
          ...toastOptions?.classNames,
        },
      }}
      {...rest}
    />
  );
}

export { Toaster };
